/**
 * Landing.tsx — Public DFA landing, full-page snap-scroll experience.
 *
 * ONE persistent 3D particle system (instanced wireframe tetrahedra, Three.js)
 * lives in a fixed canvas behind every section and MORPHS between shapes as the
 * active section changes (IntersectionObserver-driven):
 *   hero brain (right) → brain (left) → scattered → fingerprint → dual cluster →
 *   timeline → small brain. Intro = scatter that assembles into a brain.
 *
 * Particles: THREE.LineSegments over an InstancedBufferGeometry of tetrahedron
 * EDGES (1 draw call). Each instance has its own random spin axis/speed (GPU),
 * organic noise-clustered colour, varied size + opacity. A second instanced
 * object is the depth atmosphere (~500 faint tetra spread through z).
 *
 * Design system (docs/design "Lime Pulse"): pure #000, lime #a3e635, Inter,
 * 24px radius, NO shadows, NO gradients.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import * as THREE from "three";
import { MsFlo } from "../components/MsFlo.js";

const LIME = "#a3e635";
const BONE = "#ffffff";
const VOID = "#000000";
const SMOKE = "#9a9a9a";
const ASH = "#bdbdbd";
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const WA_HREF =
  "https://wa.me/6282229810868?text=" +
  encodeURIComponent("Halo DCPPI, saya ingin konsultasi analisis sidik jari (DFA).");

const WA_PEMBUKA_BAKU = "Halo DCPPI, saya ingin konsultasi analisis sidik jari (DFA).";

/** URL WhatsApp — kolom kosong memakai pembuka baku. */
export function buildWaHref(pesan: string): string {
  const trimmed = pesan.trim();
  const text = trimmed || WA_PEMBUKA_BAKU;
  return "https://wa.me/6282229810868?text=" + encodeURIComponent(text);
}

export function bukaWhatsApp(href: string): void {
  window.open(href, "_blank", "noopener,noreferrer");
}

export function tanganiEnterChat(
  e: { key: string; preventDefault: () => void },
  href: string,
  buka: (href: string) => void = bukaWhatsApp,
): void {
  if (e.key === "Enter") {
    e.preventDefault();
    buka(href);
  }
}

export const CHAT_SAPAAN_BARIS1 = "Halo Bu, halo Pak. Saya Ms. Flo — saya AI, bukan orang.";
export const CHAT_SAPAAN_BARIS2 =
  "Silakan tulis pertanyaannya di sini. Nanti saya teruskan ke tim DFA lewat WhatsApp, dan yang membalas orangnya langsung.";

// Palette as linear-ish rgb triples (on black, NormalBlending).
const PAL = {
  lime: [0xa3 / 255, 0xe6 / 255, 0x35 / 255],
  amber: [0xff / 255, 0xb8 / 255, 0x29 / 255],
  white: [1, 1, 1],
  gray: [0x9a / 255, 0x9a / 255, 0x9a / 255],
  lichen: [0x15 / 255, 0x84 / 255, 0x6e / 255],
  pink: [0xff / 255, 0x6e / 255, 0xb4 / 255],
} as const;

// ── tiny value noise (organic colour clusters + gyri) ──────────────────
function hash3(i: number, j: number, k: number): number {
  // 32-bit safe integer hash (Math.imul wraps — no float-precision overflow).
  let n = Math.imul(i, 374761393) + Math.imul(j, 668265263) + Math.imul(k, 2147483647);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  n = (n ^ (n >>> 16)) >>> 0;
  return n / 4294967295; // [0,1]
}
function smooth(t: number): number { return t * t * (3 - 2 * t); }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function vnoise(x: number, y: number, z: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = x - xi, yf = y - yi, zf = z - zi;
  const u = smooth(xf), v = smooth(yf), w = smooth(zf);
  const c000 = hash3(xi, yi, zi), c100 = hash3(xi + 1, yi, zi);
  const c010 = hash3(xi, yi + 1, zi), c110 = hash3(xi + 1, yi + 1, zi);
  const c001 = hash3(xi, yi, zi + 1), c101 = hash3(xi + 1, yi, zi + 1);
  const c011 = hash3(xi, yi + 1, zi + 1), c111 = hash3(xi + 1, yi + 1, zi + 1);
  const x00 = lerp(c000, c100, u), x10 = lerp(c010, c110, u);
  const x01 = lerp(c001, c101, u), x11 = lerp(c011, c111, u);
  return lerp(lerp(x00, x10, v), lerp(x01, x11, v), w); // [0,1]
}

// ── SDF helpers for the brain silhouette ───────────────────────────────
function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}
function sdEllipsoid(px: number, py: number, pz: number, cx: number, cy: number, cz: number, rx: number, ry: number, rz: number): number {
  const x = px - cx, y = py - cy, z = pz - cz;
  const k0 = Math.hypot(x / rx, y / ry, z / rz);
  const k1 = Math.hypot(x / (rx * rx), y / (ry * ry), z / (rz * rz));
  return k1 > 1e-6 ? (k0 * (k0 - 1)) / k1 : k0 - 1;
}
function sdSphere(px: number, py: number, pz: number, cx: number, cy: number, cz: number, r: number): number {
  return Math.hypot(px - cx, py - cy, pz - cz) - r;
}
/** Lateral brain silhouette: cerebrum ellipsoid smooth-unioned with a frontal
 *  lobe (front-up) and a temporal lobe (down). Frontal faces -x (screen left). */
function mainBrainSDF(px: number, py: number, pz: number): number {
  const cer = sdEllipsoid(px, py, pz, 0, 0, 0, 1.4, 1.0, 0.82);  // widened L/R (was 0.72) → less slab-like
  const fro = sdSphere(px, py, pz, -0.75, 0.12, 0, 0.56);        // frontal lobe (front, rounded)
  const tem = sdSphere(px, py, pz, -0.28, -0.5, 0, 0.44);        // temporal lobe (lower-mid, smaller/raised)
  const occ = sdSphere(px, py, pz, 0.86, 0.08, 0, 0.52);         // occipital fullness (back)
  // Sharper smooth-unions → lobes protrude as distinct bulges (not one oval).
  let m = smin(cer, fro, 0.04);
  m = smin(m, tem, 0.05);
  m = smin(m, occ, 0.08);
  // Lower-FRONT: scoop the underside with a sphere (organic concave) → flatter,
  // emptier orbital base WITHOUT a hard flat "cake-slice" cut.
  const carve = sdSphere(px, py, pz, -0.55, -1.3, 0, 0.95);
  return -smin(-m, carve, 0.3); // wide smooth subtraction → no sharp cut mark
}

// ── shape generators (each fills a Float32Array(n*3)) ──────────────────
function genBrain(n: number, shellOut?: Float32Array): Float32Array {
  // Rejection-sampled from the SDF above. shellOut[i] = 1 surface, 0 sparse
  // interior, -1 brain stem. Frontal lobe at -x (screen left), cerebellum at
  // lower-right (separate, no overlap with the main mass).
  const a = new Float32Array(n * 3);
  const SC = 0.62; // scale the SDF (unit ≈ half-height) to the scene
  const stemN = Math.min(60, Math.round(n * 0.02)); // longer/clearer stem (per video)
  const cerebellumN = Math.round(n * 0.07);
  const cerebrumN = n - cerebellumN - stemN;
  let i = 0;
  // ── main brain (cerebrum + frontal + temporal); curved underside, no floor cut ──
  for (; i < cerebrumN; i++) {
    let px = 0, py = 0, pz = 0, isShell = true;
    for (let t = 0; t < 110; t++) {
      px = (Math.random() * 2 - 1) * 1.6;
      py = -1.25 + Math.random() * 2.35;
      pz = (Math.random() * 2 - 1) * 0.95;
      const d = mainBrainSDF(px, py, pz);
      const shellCand = Math.abs(d) < 0.04;
      const interiorCand = d < -0.04 && Math.random() < 0.12; // denser interior fill
      if (!shellCand && !interiorCand) continue;
      // Lateral (Sylvian) sulcus: thin a diagonal band on the surface → a groove
      // separating the temporal lobe from the rest, like the reference.
      const su = Math.abs((py - 0.05) - 0.32 * (px + 0.2));
      if (shellCand && su < 0.06 && py < 0.45 && Math.random() < 0.6) continue;
      // Longitudinal (interhemispheric) fissure: carve a midline groove along the
      // TOP (z = the left/right axis) so the two hemispheres read as separate
      // from the top and front — deeper at the centre, tapering to the poles.
      const fw = 0.085 - 0.03 * Math.min(1, Math.abs(px));
      if (shellCand && Math.abs(pz) < fw && py > -0.18 && Math.random() < 0.93) continue;
      isShell = shellCand;
      break;
    }
    // Gyri texture: nudge outward by three octaves of noise → pronounced folds.
    const g = (vnoise(px * 3.4 + 2, py * 3.4 + 7, pz * 3.4 + 4) - 0.5) * 0.18
      + (vnoise(px * 7 + 1, py * 7 + 5, pz * 7 + 2) - 0.5) * 0.10
      + (vnoise(px * 13 + 3, py * 13 + 9, pz * 13 + 6) - 0.5) * 0.045;
    const nl = Math.hypot(px, py, pz) || 1;
    px += (px / nl) * g; py += (py / nl) * g; pz += (pz / nl) * g;
    if (shellOut) shellOut[i] = isShell ? 1 : 0;
    a[i * 3] = px * SC; a[i * 3 + 1] = py * SC; a[i * 3 + 2] = pz * SC;
  }
  // ── cerebellum (separate sphere at the lower-BACK, no overlap with main mass) ──
  for (; i < cerebrumN + cerebellumN; i++) {
    let px = 0, py = 0, pz = 0, isShell = true;
    for (let t = 0; t < 60; t++) {
      const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, sq = Math.sqrt(1 - u * u);
      const surf = Math.random() < 0.85;
      const rr = (surf ? 0.85 + Math.random() * 0.15 : Math.random() * 0.85) * 0.34;
      px = 0.95 + sq * Math.cos(th) * rr; py = -0.78 + sq * Math.sin(th) * rr; pz = u * rr;
      if (mainBrainSDF(px, py, pz) > 0.1) { isShell = surf; break; } // clear notch (transverse fissure)
    }
    if (shellOut) shellOut[i] = isShell ? 1 : 0;
    a[i * 3] = px * SC; a[i * 3 + 1] = py * SC; a[i * 3 + 2] = pz * SC;
  }
  // ── brain stem (longer thin capsule descending from the centre-bottom) ──
  for (; i < n; i++) {
    if (shellOut) shellOut[i] = -1;
    const t = Math.random(), ang = Math.random() * Math.PI * 2, rr = (0.09 - t * 0.05) * Math.sqrt(Math.random());
    const px = 0.12 + Math.cos(ang) * rr;
    const py = -0.7 - t * 0.5;          // -0.7 → -1.2 (longer)
    const pz = Math.sin(ang) * rr;
    a[i * 3] = px * SC; a[i * 3 + 1] = py * SC; a[i * 3 + 2] = pz * SC;
  }
  return a;
}

/** Globe (Earth) — ocean sphere (dim) with the main Indonesian islands picked
 *  out in bright lime. Returns positions + a per-particle globe colour. */
function genGlobe(n: number): { pos: Float32Array; col: Float32Array; idnN: number } {
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const R = 1.25;
  // [lonMin, lonMax, latMin, latMax] for the major islands.
  const ID: Array<[number, number, number, number]> = [
    [95, 106, -6, 6],    // Sumatera
    [106, 115, -9, -6],  // Jawa
    [108, 117, -4, 5],   // Kalimantan
    [118, 125, -6, 2],   // Sulawesi
    [131, 141, -9, 0],   // Papua
  ];
  // Rough continent boxes [lonMin, lonMax, latMin, latMax] — all landmasses, so
  // the globe reads as a world map (brighter land vs dim ocean).
  const LAND: Array<[number, number, number, number]> = [
    [-168, -52, 30, 72], [-125, -75, 14, 50], [-92, -58, 8, 22], // N. America + Mexico/CentralAm
    [-82, -34, -56, 12],                                          // S. America
    [-58, -12, 60, 83],                                           // Greenland
    [-11, 2, 50, 59], [-10, 40, 36, 71],                          // UK + Europe
    [-18, 52, -35, 37],                                           // Africa
    [26, 100, 8, 78], [60, 180, 45, 78],                          // Asia (south + Siberia)
    [68, 90, 6, 35],                                              // India
    [95, 141, -10, 9],                                            // SE Asia / Indonesia
    [129, 146, 30, 46],                                           // Japan
    [113, 154, -39, -10],                                         // Australia
    [166, 179, -47, -34],                                         // New Zealand
    [43, 51, -26, -12],                                           // Madagascar
  ];
  const isLand = (lat: number, lon: number) =>
    LAND.some((b) => lon >= b[0] && lon <= b[1] && lat >= b[2] && lat <= b[3]);
  const idnN = Math.min(Math.round(n * 0.18), 1200); // bright Indonesia highlight
  const toXyz = (lon: number, lat: number) => {
    const phi = (lon - 118) * Math.PI / 180;  // centre Indonesia toward camera (+z)
    const theta = lat * Math.PI / 180;
    return [Math.cos(theta) * Math.sin(phi) * R, Math.sin(theta) * R, Math.cos(theta) * Math.cos(phi) * R];
  };
  for (let i = 0; i < n; i++) {
    if (i < idnN) { // Indonesia — bright lime
      const reg = ID[Math.floor(Math.random() * ID.length)]!;
      const lon = reg[0] + Math.random() * (reg[1] - reg[0]);
      const lat = reg[2] + Math.random() * (reg[3] - reg[2]);
      const p = toXyz(lon, lat);
      pos[i * 3] = p[0]! * 1.01; pos[i * 3 + 1] = p[1]! * 1.01; pos[i * 3 + 2] = p[2]! * 1.01;
      col[i * 3] = PAL.lime[0]; col[i * 3 + 1] = PAL.lime[1]; col[i * 3 + 2] = PAL.lime[2];
    } else { // rest of the world — land (bright) vs ocean (dim)
      const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2;
      const sq = Math.sqrt(1 - u * u);
      const x = sq * Math.cos(th), y = u, z = sq * Math.sin(th);
      pos[i * 3] = x * R; pos[i * 3 + 1] = y * R; pos[i * 3 + 2] = z * R;
      const lat = Math.asin(y) * 180 / Math.PI;
      const lon = Math.atan2(x, z) * 180 / Math.PI + 118; // inverse of toXyz centring
      const lonN = ((lon + 180) % 360 + 360) % 360 - 180;
      if (isLand(lat, lonN)) { // land — pale teal/white, clearly brighter
        col[i * 3] = 0.62; col[i * 3 + 1] = 0.85; col[i * 3 + 2] = 0.72;
      } else { // ocean — dim but visible so the full sphere reads
        col[i * 3] = 0.12; col[i * 3 + 1] = 0.24; col[i * 3 + 2] = 0.3;
      }
    }
  }
  return { pos, col, idnN };
}

function genScattered(n: number): Float32Array {
  // ~60% pushed far back (fog-dimmed → barely visible), ~40% in the mid field →
  // a sparse, readable scatter rather than a dense field.
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    if (Math.random() < 0.6) {
      a[i * 3] = (Math.random() - 0.5) * 11;
      a[i * 3 + 1] = (Math.random() - 0.5) * 7;
      a[i * 3 + 2] = -3 - Math.random() * 6;   // far behind → faint
    } else {
      a[i * 3] = (Math.random() - 0.5) * 6;
      a[i * 3 + 1] = (Math.random() - 0.5) * 4;
      a[i * 3 + 2] = (Math.random() - 0.5) * 3;
    }
  }
  return a;
}

function genFingerprint(n: number): Float32Array {
  // Loop pattern via the orientation field of a {core, delta} pair. Ridges are
  // streamlines of that field. SPARSE seeds + LONG traces → distinct, spaced
  // ridges (visible gaps), a spiral CORE and a triangular DELTA divergence.
  const core = { x: 0.14, y: 0.2 };
  const delta = { x: -0.06, y: -0.62 };
  const ang = (x: number, y: number) =>
    0.5 * (Math.atan2(y - core.y, x - core.x) - Math.atan2(y - delta.y, x - delta.x)) + 0.25;
  const ridges: Array<Array<[number, number]>> = [];
  const h = 0.016, seg = 95;
  // Seed on two perpendicular spaced lines so the whole disk is combed once,
  // with ridge spacing ~0.11 (gaps stay visible).
  const seeds: Array<[number, number]> = [];
  for (let t = -1.05; t <= 1.05; t += 0.11) {
    seeds.push([t, 0.02]);
    seeds.push([0.02, t]);
  }
  for (const [sx0, sy0] of seeds) {
    const line: Array<[number, number]> = [];
    for (const dir of [1, -1]) {
      let x = sx0, y = sy0, pa: number | null = null;
      for (let s = 0; s < seg; s++) {
        let aN = ang(x, y);
        let vx = Math.cos(aN), vy = Math.sin(aN);
        if (pa !== null && vx * Math.cos(pa) + vy * Math.sin(pa) < 0) { aN += Math.PI; vx = -vx; vy = -vy; }
        pa = aN;
        x += dir * vx * h; y += dir * vy * h;
        if (x * x + y * y > 1.25) break;
        line.push([x, y]);
      }
    }
    if (line.length > 6) ridges.push(line);
  }
  // Walk ridges by arc length, placing particles at even spacing along each.
  const along = 0.02;
  const pts: Array<[number, number]> = [];
  for (const line of ridges) {
    let acc = 0;
    for (let i = 1; i < line.length; i++) {
      const a0 = line[i - 1]!, a1 = line[i]!;
      const d = Math.hypot(a1[0] - a0[0], a1[1] - a0[1]);
      acc += d;
      if (acc >= along) {
        acc = 0;
        if (Math.random() > 0.04) pts.push(a1); // ~4% gaps (broken ridges)
      }
    }
  }
  const a = new Float32Array(n * 3);
  const m = Math.max(1, pts.length);
  for (let i = 0; i < n; i++) {
    const p = pts[i % m] ?? [0, 0];
    const px = p[0] + (Math.random() - 0.5) * 0.01; // tiny wobble
    const py = p[1] + (Math.random() - 0.5) * 0.01;
    a[i * 3] = px * 1.4;
    a[i * 3 + 1] = py * 1.4;
    a[i * 3 + 2] = (vnoise(px * 4 + 9, py * 4 + 2, 0.5) - 0.5) * 0.16;
  }
  return a;
}

function genDual(n: number): Float32Array {
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const side = i < n / 2 ? -1 : 1;
    const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2;
    const sq = Math.sqrt(1 - u * u);
    const r = (0.35 + 0.65 * Math.sqrt(Math.random()));
    a[i * 3] = side * 1.85 + sq * Math.cos(th) * 0.5 * r;
    a[i * 3 + 1] = sq * Math.sin(th) * 0.7 * r + 0.05;
    a[i * 3 + 2] = u * 0.55 * r;
  }
  return a;
}

function genTimeline(n: number): Float32Array {
  // Sits in the LOWER third of the screen (below the section text) and uses only
  // ~40% of the particles — the rest are pushed far back so they read as faint
  // depth, effectively thinning the timeline.
  const a = new Float32Array(n * 3);
  const nodes = [-2.0, -1.2, -0.4, 0.4, 1.2, 2.0];
  const yBase = -1.25;
  for (let i = 0; i < n; i++) {
    if (Math.random() < 0.6) { // pushed far → faint (fewer visible particles)
      a[i * 3] = (Math.random() - 0.5) * 11;
      a[i * 3 + 1] = (Math.random() - 0.5) * 7;
      a[i * 3 + 2] = -4 - Math.random() * 6;
      continue;
    }
    let x: number, y: number, z: number;
    if (Math.random() < 0.55) { // node clusters along the line
      const nx = nodes[Math.floor(Math.random() * nodes.length)]!;
      const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2;
      const sq = Math.sqrt(1 - u * u), r = 0.2 * Math.sqrt(Math.random());
      x = nx + sq * Math.cos(th) * r;
      y = yBase + sq * Math.sin(th) * r;
      z = u * r;
    } else { // connecting line
      x = -2.2 + Math.random() * 4.4;
      y = yBase + (Math.random() - 0.5) * 0.1;
      z = (Math.random() - 0.5) * 0.12;
    }
    a[i * 3] = x; a[i * 3 + 1] = y; a[i * 3 + 2] = z;
  }
  return a;
}

/** "DFA" wordmark — particles sampled from text rendered to a canvas. */
function genDFA(n: number): Float32Array {
  const a = new Float32Array(n * 3);
  const W = 640, H = 240;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const g = cv.getContext("2d");
  const pts: number[] = [];
  if (g) {
    g.fillStyle = "#fff";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.font = "900 190px Inter, Arial, sans-serif";
    g.fillText("DFA", W / 2, H / 2 + 6);
    const data = g.getImageData(0, 0, W, H).data;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (data[(y * W + x) * 4 + 3]! > 128) pts.push(x, y);
      }
    }
  }
  const m = pts.length / 2;
  for (let i = 0; i < n; i++) {
    const k = m > 0 ? Math.floor(Math.random() * m) * 2 : 0;
    const px = pts[k] ?? W / 2, py = pts[k + 1] ?? H / 2;
    const jx = (Math.random() - 0.5) * 1.6, jy = (Math.random() - 0.5) * 1.6;
    a[i * 3] = ((px + jx) / W - 0.5) * 3.0;
    a[i * 3 + 1] = -((py + jy) / H - 0.5) * 1.0 + 1.0; // shifted up → top of screen
    a[i * 3 + 2] = (Math.random() - 0.5) * 0.12;
  }
  return a;
}

/** Spiral galaxy (Milky Way) silhouette — 4 logarithmic arms + central bulge +
 *  faint disk haze. Lies in the XY plane (normal = Z) so it spins around its axis. */
function genGalaxy(n: number): Float32Array {
  const a = new Float32Array(n * 3);
  const ARMS = 4;
  const TWIST = 2.4;       // log-spiral tightness
  for (let i = 0; i < n; i++) {
    const r = Math.random();
    if (r < 0.2) { // central bulge (flattened spheroid)
      const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, sq = Math.sqrt(1 - u * u);
      const rr = Math.pow(Math.random(), 1.8) * 0.4;
      a[i * 3] = sq * Math.cos(th) * rr;
      a[i * 3 + 1] = sq * Math.sin(th) * rr;
      a[i * 3 + 2] = u * rr * 0.4;
    } else if (r < 0.82) { // spiral arms (log spiral, scatter grows outward)
      const arm = Math.floor(Math.random() * ARMS);
      const t = Math.pow(Math.random(), 0.7);            // bias inward, long tail outward
      const radius = 0.28 + t * 1.8;
      const spread = (Math.random() - 0.5) * (0.32 * (1.15 - t)); // arm thins outward
      const ang = arm * (Math.PI * 2 / ARMS) + Math.log(1 + t * 6) * TWIST + spread;
      a[i * 3] = Math.cos(ang) * radius + (Math.random() - 0.5) * 0.06;
      a[i * 3 + 1] = Math.sin(ang) * radius + (Math.random() - 0.5) * 0.06;
      a[i * 3 + 2] = (Math.random() - 0.5) * 0.07 * (1.1 - t);
    } else { // faint disk haze + outer halo (naturalness)
      const th = Math.random() * Math.PI * 2;
      const rad = 0.3 + Math.pow(Math.random(), 0.6) * 2.0;
      a[i * 3] = Math.cos(th) * rad;
      a[i * 3 + 1] = Math.sin(th) * rad;
      a[i * 3 + 2] = (Math.random() - 0.5) * 0.18;
    }
  }
  return a;
}

const ATLAS_COLS = 6;
/** Pre-render ATLAS_COLS wireframe-tetrahedron views (different 3D angles) into
 *  a single canvas atlas. Particles are billboarded Points that sample one cell
 *  + a random 2D spin → small "triangle with inner lines" sprites, cheap at 60fps
 *  with thousands of points. Outline only — no face fill (no glass-shard look). */
function makeTetraAtlas(): THREE.Texture {
  const cell = 64;
  const cv = document.createElement("canvas");
  cv.width = cell * ATLAS_COLS; cv.height = cell;
  const g = cv.getContext("2d")!;
  g.clearRect(0, 0, cv.width, cell);
  g.strokeStyle = "#ffffff";
  g.lineWidth = 1.8; g.lineJoin = "round"; g.lineCap = "round";
  const s = 1 / Math.sqrt(3);
  const V = [[s, s, s], [s, -s, -s], [-s, s, -s], [-s, -s, s]];
  const E = [[0, 1], [0, 2], [0, 3], [1, 2], [1, 3], [2, 3]];
  for (let ci = 0; ci < ATLAS_COLS; ci++) {
    const ax = 0.6 * ci + 0.35, ay = 1.1 * ci + 0.7;
    const ca = Math.cos(ax), sa = Math.sin(ax), cb = Math.cos(ay), sb = Math.sin(ay);
    const proj = V.map((p) => {
      const x = p[0]!, y = p[1]!, z = p[2]!;
      const y1 = y * ca - z * sa, z1 = y * sa + z * ca;     // rotate X
      const x2 = x * cb + z1 * sb;                          // rotate Y
      return [x2, y1] as [number, number];
    });
    const cx = ci * cell + cell / 2, cy = cell / 2, R = cell * 0.34;
    g.beginPath();
    for (const [a, b] of E) {
      const pa = proj[a!]!, pb = proj[b!]!;
      g.moveTo(cx + pa[0] * R, cy + pa[1] * R);
      g.lineTo(cx + pb[0] * R, cy + pb[1] * R);
    }
    g.stroke();
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

const VERT = `
  attribute float aSize;
  attribute vec3 aColor;
  attribute vec3 aColor2;
  attribute float aOpacity;
  attribute float aVariant;
  attribute float aRotSpeed;
  attribute float aPhase;
  attribute vec3 aNormal;
  attribute vec3 aDrift;
  uniform float uTime;
  uniform float uPixel;
  uniform float uColorMix;   // 0 = base palette, 1 = globe palette
  uniform float uDepthMix;   // 0 = flat, 1 = front-bright/back-dim (brain/globe)
  uniform float uCenterDim;  // 1 = darken screen-centre particles (scatter sections)
  uniform float uSizeMul;    // per-section sprite size multiplier (fingerprint = small)
  varying vec3 vColor;
  varying float vOpacity;
  varying float vVariant;
  varying float vAngle;
  varying float vFog;
  varying float vRim;
  varying float vDepthOp;
  varying float vCenterDim;
  void main() {
    vec3 c = position;
    // Each particle drifts along its OWN random axis → no synchronised motion.
    c += aDrift * sin(uTime * 0.5 + aPhase);
    vec4 mv = modelViewMatrix * vec4(c, 1.0);
    float depth = -mv.z;
    // Facing: +1 = surface faces the camera (front), -1 = back. Drives the
    // front-bright / back-dim depth shading + a silhouette rim.
    vec3 nrm = normalize((modelViewMatrix * vec4(aNormal, 0.0)).xyz);
    vec3 vd = normalize(-mv.xyz);
    float facing = dot(nrm, vd);
    float rim = pow(1.0 - abs(facing), 2.5);
    vRim = rim;
    // Sharp depth contrast: front faces full bright, back fades to ~0.08.
    float fr = facing * 0.5 + 0.5;
    float depthOp = clamp(0.08 + fr * fr * 0.92, 0.08, 1.0);
    vDepthOp = mix(1.0, depthOp, uDepthMix);
    float sizeFac = mix(1.0, clamp(0.75 + facing * 0.3, 0.5, 1.1), uDepthMix);
    gl_PointSize = aSize * uPixel * uSizeMul * (5.0 / depth) * sizeFac * (1.0 + rim * 0.4);
    gl_Position = projectionMatrix * mv;
    // Dim particles near the screen centre (so section text stays readable).
    vec2 ndc = gl_Position.xy / max(0.0001, gl_Position.w);
    float cf = smoothstep(0.0, 0.42, length(ndc));   // 0 = centre, 1 = edge
    vCenterDim = mix(1.0, mix(0.22, 1.0, cf), uCenterDim);
    vColor = mix(aColor, aColor2, uColorMix);
    vOpacity = aOpacity;
    vVariant = aVariant;
    vAngle = uTime * aRotSpeed + aPhase;       // slow individual 2D spin
    vFog = clamp((depth - 2.0) / 8.0, 0.0, 1.0);
  }
`;

const FRAG = `
  uniform sampler2D uAtlas;
  uniform float uCols;
  uniform float uAlphaMul;
  varying vec3 vColor;
  varying float vOpacity;
  varying float vVariant;
  varying float vAngle;
  varying float vFog;
  varying float vRim;
  varying float vDepthOp;
  varying float vCenterDim;
  void main() {
    vec2 p = gl_PointCoord - 0.5;
    float s = sin(vAngle), co = cos(vAngle);
    vec2 r = vec2(p.x * co - p.y * s, p.x * s + p.y * co) + 0.5; // spin sprite
    if (r.x < 0.0 || r.x > 1.0 || r.y < 0.0 || r.y > 1.0) discard;
    float cell = floor(vVariant + 0.5);
    float al = texture2D(uAtlas, vec2((r.x + cell) / uCols, r.y)).a;
    if (al < 0.08) discard;
    vec3 col = mix(vColor, vec3(1.0), vRim * 0.4);            // edge → silvery
    float a = al * vOpacity * vDepthOp * vCenterDim * uAlphaMul * mix(1.0, 0.4, vFog) * (1.0 + vRim * 0.6);
    gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  }
`;

type ShapeKey = "brain" | "scattered" | "fingerprint" | "dual" | "timeline" | "globe" | "dfa" | "galaxy";
interface SectionCfg { shape: ShapeKey; x: number; scale: number; }
const SECTIONS: SectionCfg[] = [
  { shape: "brain", x: 0.85, scale: 1.55 },      // 0 hero — full brain in frame
  { shape: "brain", x: -0.85, scale: 1.75 },     // 1 apa itu — full brain, opposite side
  { shape: "scattered", x: 0, scale: 1.0 },      // 2 filosofi
  { shape: "fingerprint", x: -1.15, scale: 1.08 }, // 3 cara kerja
  { shape: "dfa", x: 0, scale: 1.15 },           // 4 untuk siapa — "DFA" wordmark
  { shape: "timeline", x: 0, scale: 1.0 },       // 5 timeline
  { shape: "globe", x: 0, scale: 2.2 },          // 6 globe + Indonesia (large)
  { shape: "galaxy", x: 0, scale: 1.6 },         // 7 cta — rotating Milky-Way silhouette
];

interface Controller { setSection: (i: number) => void; }

function Particles({ ctrlRef }: { ctrlRef: React.MutableRefObject<Controller | null> }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const mobile = window.innerWidth < 880;
    const N = mobile ? 5000 : 14000;      // dense small sprites (fine ridges + full brain)
    const ATM = mobile ? 280 : 700;
    const pixel = Math.min(window.devicePixelRatio || 1, 2);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    } catch (err) {
      console.warn("WebGL unavailable; particle system disabled.", err);
      return;
    }
    renderer.setPixelRatio(pixel);
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.z = 5;

    const atlas = makeTetraAtlas();
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 }, uPixel: { value: pixel },
        uAtlas: { value: atlas }, uCols: { value: ATLAS_COLS },
        uColorMix: { value: 0 }, uDepthMix: { value: 1 }, uCenterDim: { value: 0 },
        uSizeMul: { value: 1 }, uAlphaMul: { value: 1 },
      },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.NormalBlending,
    });
    const uTimeU = mat.uniforms.uTime!;
    const uColorMixU = mat.uniforms.uColorMix!;
    const uDepthMixU = mat.uniforms.uDepthMix!;
    const uCenterDimU = mat.uniforms.uCenterDim!;
    const uSizeMulU = mat.uniforms.uSizeMul!;
    const uAlphaMulU = mat.uniforms.uAlphaMul!;

    // Precompute every shape target.
    const brainShell = new Float32Array(N);
    const globe = genGlobe(N);
    const shapes: Record<ShapeKey, Float32Array> = {
      brain: genBrain(N, brainShell),
      scattered: genScattered(N),
      fingerprint: genFingerprint(N),
      dual: genDual(N),
      timeline: genTimeline(N),
      globe: globe.pos,
      dfa: genDFA(N),
      galaxy: genGalaxy(N),
    };

    // Per-particle static attributes (colour clusters, size, atlas variant, spin).
    const color = new Float32Array(N * 3);
    const color2 = globe.col;               // globe-mode palette (Indonesia lime)
    const size = new Float32Array(N);
    const opacity = new Float32Array(N);
    const variant = new Float32Array(N);
    const rotSpeed = new Float32Array(N);
    const phase = new Float32Array(N);
    const delay = new Float32Array(N);
    const normal = new Float32Array(N * 3);
    const drift = new Float32Array(N * 3);
    const brain = shapes.brain;
    for (let i = 0; i < N; i++) {
      const bx = brain[i * 3]!, by = brain[i * 3 + 1]!, bz = brain[i * 3 + 2]!;
      // White/silver is the scattered base (~50%, even spread); the rest are
      // noise-CLUSTERED lime / amber / lichen patches across the form.
      const nC = vnoise(bx * 1.8 + 10, by * 1.8 + 20, bz * 1.8 + 30);
      const nP = vnoise(bx * 1.8 - 30, by * 1.8 + 50, bz * 1.8 + 15); // pink cluster noise
      let c: readonly number[];
      if (Math.random() < 0.42) c = nP > 0.6 ? PAL.pink : PAL.white; // more saturated mix (vivid)
      else if (nC < 0.5) c = PAL.lime;
      else if (nC < 0.8) c = PAL.amber;
      else c = PAL.lichen;
      color[i * 3] = c[0]!; color[i * 3 + 1] = c[1]!; color[i * 3 + 2] = c[2]!;
      const shellV = brainShell[i]!;
      const isStem = shellV < 0;
      const shell = shellV > 0.5;
      const isIndo = i < globe.idnN;          // these indices = Indonesia in globe mode
      // Shell emphasis: surface bright + normal size; interior dim + 0.6× size;
      // stem subtle. Bright dense surface + a softer, dimmer interior fill (volume).
      const baseSize = 9 + Math.random() * 8;
      size[i] = isStem ? 6 : shell ? baseSize : baseSize * 0.62;
      opacity[i] = isStem ? 0.3
        : shell ? 0.8 + Math.random() * 0.2
          : 0.2 + Math.random() * 0.2; // interior "agak redup" (visible, < surface)
      if ((c === PAL.white || c === PAL.pink) && shell) opacity[i] = Math.max(opacity[i]!, 0.6);
      // Indonesia particles: bright but SMALL (0.6×) so they read as a fine
      // archipelago silhouette, not a blob. (Only matters in globe mode.)
      if (isIndo) { opacity[i] = 0.85 + Math.random() * 0.15; size[i] = size[i]! * 0.6; }
      // Approx outward normal (from brain centroid) for depth lighting + rim.
      const nx = bx + 0.06, ny = by + 0.05, nz = bz;
      const nl = Math.hypot(nx, ny, nz) || 1;
      normal[i * 3] = nx / nl; normal[i * 3 + 1] = ny / nl; normal[i * 3 + 2] = nz / nl;
      // Random per-particle drift axis (varied directions, never synchronised).
      const dxr = Math.random() * 2 - 1, dyr = Math.random() * 2 - 1, dzr = Math.random() * 2 - 1;
      const dl = Math.hypot(dxr, dyr, dzr) || 1;
      const amp = 0.015 + Math.random() * 0.03;
      drift[i * 3] = (dxr / dl) * amp; drift[i * 3 + 1] = (dyr / dl) * amp; drift[i * 3 + 2] = (dzr / dl) * amp;
      variant[i] = Math.floor(Math.random() * ATLAS_COLS);
      rotSpeed[i] = (0.1 + Math.random() * 0.4) * (Math.random() < 0.5 ? -1 : 1);
      phase[i] = Math.random() * 6.2831;
      // Top-down shockwave: stagger by the particle's HEIGHT in the brain —
      // top of the dome arrives first, the base last. (Brain y ≈ -0.05..0.85.)
      const ynorm = Math.max(-1, Math.min(1, by / 0.62)); // -1 base, +1 top of dome
      delay[i] = ((1 - ynorm) / 2) * 0.4;
    }

    const center = new Float32Array(N * 3); // position attribute, animated each frame
    center.set(shapes.scattered);           // intro starts scattered

    const geo = new THREE.BufferGeometry();
    const centerAttr = new THREE.BufferAttribute(center, 3);
    centerAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("position", centerAttr);
    geo.setAttribute("aColor", new THREE.BufferAttribute(color, 3));
    geo.setAttribute("aColor2", new THREE.BufferAttribute(color2, 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(size, 1));
    geo.setAttribute("aOpacity", new THREE.BufferAttribute(opacity, 1));
    geo.setAttribute("aVariant", new THREE.BufferAttribute(variant, 1));
    geo.setAttribute("aRotSpeed", new THREE.BufferAttribute(rotSpeed, 1));
    geo.setAttribute("aPhase", new THREE.BufferAttribute(phase, 1));
    geo.setAttribute("aNormal", new THREE.BufferAttribute(normal, 3));
    geo.setAttribute("aDrift", new THREE.BufferAttribute(drift, 3));
    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.renderOrder = 1;
    scene.add(points);

    // ── Atmosphere: SAME small wireframe sprites, just dimmer + sparse across
    //    the whole field. Only the first 3 are slightly larger foreground motes. ──
    const aCenter = new Float32Array(ATM * 3);
    const aColor = new Float32Array(ATM * 3);
    const aSize = new Float32Array(ATM);
    const aOpac = new Float32Array(ATM);
    const aVar = new Float32Array(ATM);
    const aSpeed = new Float32Array(ATM);
    const aPhase = new Float32Array(ATM);
    const aNorm = new Float32Array(ATM * 3);
    const aDrift = new Float32Array(ATM * 3);
    for (let i = 0; i < ATM; i++) {
      const big = i < 5; // max 5 statement motes (25-35px); the rest small + uniform
      // Normal faces the camera so depth-lighting keeps atmosphere bright.
      aNorm[i * 3] = 0; aNorm[i * 3 + 1] = 0; aNorm[i * 3 + 2] = 1;
      const dxr = Math.random() * 2 - 1, dyr = Math.random() * 2 - 1, dzr = Math.random() * 2 - 1;
      const dl = Math.hypot(dxr, dyr, dzr) || 1, amp = 0.04 + Math.random() * 0.07; // livelier drift
      aDrift[i * 3] = (dxr / dl) * amp; aDrift[i * 3 + 1] = (dyr / dl) * amp; aDrift[i * 3 + 2] = (dzr / dl) * amp;
      aCenter[i * 3] = (Math.random() - 0.5) * 9;
      aCenter[i * 3 + 1] = (Math.random() - 0.5) * 5.5;
      aCenter[i * 3 + 2] = big ? 1.8 + Math.random() * 2 : -1 - Math.random() * 6;
      const c = Math.random() < 0.5 ? PAL.white : (Math.random() < 0.5 ? PAL.lime : PAL.amber);
      aColor[i * 3] = c[0]!; aColor[i * 3 + 1] = c[1]!; aColor[i * 3 + 2] = c[2]!;
      aSize[i] = big ? 25 + Math.random() * 12 : 7 + Math.random() * 14;   // small, slightly varied
      aOpac[i] = big ? 0.16 + Math.random() * 0.12 : 0.4 + Math.random() * 0.32; // brighter, more alive
      aVar[i] = Math.floor(Math.random() * ATLAS_COLS);
      aSpeed[i] = (0.1 + Math.random() * 0.35) * (Math.random() < 0.5 ? -1 : 1);
      aPhase[i] = Math.random() * 6.2831;
    }
    const ageo = new THREE.BufferGeometry();
    ageo.setAttribute("position", new THREE.BufferAttribute(aCenter, 3));
    ageo.setAttribute("aColor", new THREE.BufferAttribute(aColor, 3));
    ageo.setAttribute("aColor2", new THREE.BufferAttribute(aColor, 3));
    ageo.setAttribute("aSize", new THREE.BufferAttribute(aSize, 1));
    ageo.setAttribute("aOpacity", new THREE.BufferAttribute(aOpac, 1));
    ageo.setAttribute("aVariant", new THREE.BufferAttribute(aVar, 1));
    ageo.setAttribute("aRotSpeed", new THREE.BufferAttribute(aSpeed, 1));
    ageo.setAttribute("aPhase", new THREE.BufferAttribute(aPhase, 1));
    ageo.setAttribute("aNormal", new THREE.BufferAttribute(aNorm, 3));
    ageo.setAttribute("aDrift", new THREE.BufferAttribute(aDrift, 3));
    const atmo = new THREE.Points(ageo, mat);
    atmo.frustumCulled = false;
    atmo.renderOrder = 0;
    scene.add(atmo);

    function resize() {
      const w = window.innerWidth, h = window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener("resize", resize);

    // ── morph state ──
    const morphFrom = new Float32Array(N * 3);
    morphFrom.set(shapes.scattered);
    let morphTo = shapes.brain;
    let mStart = performance.now();
    let mDur = 2000; // intro assembly (top-down shockwave ~2s)
    const MAXD = 0.4;
    const cubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    let curX = mobile ? 0 : SECTIONS[0]!.x;
    let curScale = SECTIONS[0]!.scale;
    let tgtX = curX, tgtScale = curScale;
    let activeIdx = 0;
    let curColorMix = 0, tgtColorMix = 0; // globe palette
    let curDepthMix = 1, tgtDepthMix = 1; // front-bright depth shading
    let curCenterDim = 0, tgtCenterDim = 0; // dark centre for scatter sections
    let curSizeMul = 1, tgtSizeMul = 1;     // smaller sprites for the fingerprint
    let globeMode = false, brainMode = true, globeSpin = 0, brainRotY = 0, brainRotX = 0;
    let galaxyMode = false, galaxySpin = 0;
    let curAlpha = 1, tgtAlpha = 1;            // galaxy = semi-transparent
    let curSpinY = 0.0014, tgtSpinY = 0.0014; // S1: +Y. S2: −Y (opposite)
    let curSpinX = 0, tgtSpinX = 0;           // S2 also tilts slowly on X
    let curFlatRot = 0; // flat shapes ease their Y rotation back to 0

    ctrlRef.current = {
      setSection(i: number) {
        const cfg = SECTIONS[i];
        if (!cfg || i === activeIdx) return;
        activeIdx = i;
        morphFrom.set(center);          // snapshot current → smooth chaining
        morphTo = shapes[cfg.shape];
        mStart = performance.now();
        mDur = 1800;
        tgtX = mobile ? 0 : cfg.x;
        tgtScale = cfg.scale;
        globeMode = cfg.shape === "globe";
        brainMode = cfg.shape === "brain";
        galaxyMode = cfg.shape === "galaxy";
        tgtColorMix = globeMode ? 1 : 0;
        tgtDepthMix = brainMode || globeMode ? 1 : 0;
        tgtAlpha = galaxyMode ? 0.5 : 1;            // galaxy is semi-transparent
        tgtCenterDim = cfg.shape === "scattered" ? 1 : 0;
        tgtSizeMul = cfg.shape === "dual" ? 1.8          // dense, clear twin clusters
          : 1;                                           // fingerprint back to normal size
        // Slow horizontal Y spin (video brain stays near-lateral); S2 reverses.
        tgtSpinY = i === 1 ? -0.0008 : 0.0008;
        tgtSpinX = 0;
      },
    };

    function writeCenters(now: number) {
      const p = Math.min(1, (now - mStart) / mDur);
      for (let i = 0; i < N; i++) {
        const t = Math.max(0, Math.min(1, (p - delay[i]!) / (1 - MAXD)));
        const e = cubic(t);
        const k = i * 3;
        center[k] = lerp(morphFrom[k]!, morphTo[k]!, e);
        center[k + 1] = lerp(morphFrom[k + 1]!, morphTo[k + 1]!, e);
        center[k + 2] = lerp(morphFrom[k + 2]!, morphTo[k + 2]!, e);
      }
      centerAttr.needsUpdate = true;
    }

    let raf = 0;
    if (reduce) {
      morphFrom.set(shapes.brain); center.set(shapes.brain);
      centerAttr.needsUpdate = true;
      points.position.x = curX; points.scale.setScalar(curScale);
      renderer.render(scene, camera);
    } else {
      const loop = (now: number) => {
        writeCenters(now);
        curX += (tgtX - curX) * 0.06;
        curScale += (tgtScale - curScale) * 0.06;
        points.position.x = curX;
        points.scale.setScalar(curScale);
        curColorMix += (tgtColorMix - curColorMix) * 0.06;
        curDepthMix += (tgtDepthMix - curDepthMix) * 0.06;
        curCenterDim += (tgtCenterDim - curCenterDim) * 0.06;
        curSizeMul += (tgtSizeMul - curSizeMul) * 0.06;
        curSpinY += (tgtSpinY - curSpinY) * 0.045;  // ~1.2s lerp between S1/S2 modes
        curSpinX += (tgtSpinX - curSpinX) * 0.045;
        const ts = now * 0.001;
        if (globeMode) {
          globeSpin += 0.0012;                    // globe spins slowly on Y
          points.rotation.y = globeSpin;
          points.rotation.x = 0.05;
        } else if (brainMode) {
          // S1: spin on Y (horizontal). S2: spin on X (a forward/back wheel).
          // Speed up 2× near each end-on view so the "balloon" phase is brief.
          let sy = curSpinY, sx = curSpinX;
          if (Math.abs(Math.sin(brainRotY)) > 0.85) sy *= 2;
          if (Math.abs(Math.sin(brainRotX)) > 0.85) sx *= 2;
          brainRotY += sy;
          brainRotX += sx;
          points.rotation.y = brainRotY;
          points.rotation.x = 0.12 + brainRotX;
        } else if (galaxyMode) {
          // Disk lies in XY (normal = Z). Euler order XYZ applies Z innermost, so
          // rotation.z spins the galaxy IN ITS OWN PLANE; rotation.x tilts it.
          // Negative tilt → seen from ABOVE; negative spin → reversed direction.
          galaxySpin += 0.0016;
          points.rotation.z = -galaxySpin;
          points.rotation.x += (-0.65 - points.rotation.x) * 0.06; // tilt to view from above
          points.rotation.y = 0;
        } else {
          // Flat shapes (fingerprint/dual/timeline/scatter) face the camera.
          curFlatRot += (0 - curFlatRot) * 0.08;
          points.rotation.y = curFlatRot;
          points.rotation.x += (0 - points.rotation.x) * 0.08;
        }
        if (!galaxyMode) points.rotation.z += (0 - points.rotation.z) * 0.08; // reset spin axis
        curAlpha += (tgtAlpha - curAlpha) * 0.06;
        uColorMixU.value = curColorMix;
        uDepthMixU.value = curDepthMix;
        uCenterDimU.value = curCenterDim;
        uSizeMulU.value = curSizeMul;
        uAlphaMulU.value = curAlpha;
        uTimeU.value = ts;
        renderer.render(scene, camera);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      ctrlRef.current = null;
      geo.dispose(); ageo.dispose(); mat.dispose(); renderer.dispose();
    };
  }, [ctrlRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "fixed", inset: 0, width: "100%", height: "100%", display: "block", zIndex: 0, pointerEvents: "none" }}
    />
  );
}

// ── presentational helpers ─────────────────────────────────────────────
function Kicker({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.22em", color: LIME, marginBottom: 20 }}>{children}</div>;
}
const cardStyle: React.CSSProperties = { border: "1px solid rgba(255,255,255,0.12)", borderRadius: 24, padding: 28, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" };
const ctaPrimary: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: VOID, background: LIME, borderRadius: 24, padding: "14px 22px", textDecoration: "none" };
const ctaGhost: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: ASH, border: "1px solid rgba(255,255,255,0.25)", borderRadius: 24, padding: "14px 22px", textDecoration: "none" };

/** Custom cursor: a lime ring that trails the mouse and grows over interactives. */
function CursorFX() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let x = window.innerWidth / 2, y = window.innerHeight / 2, tx = x, ty = y, raf = 0;
    const onMove = (e: MouseEvent) => { tx = e.clientX; ty = e.clientY; };
    const onOver = (e: MouseEvent) => {
      const t = e.target as Element | null;
      setHover(!!(t && t.closest && t.closest('a,button,[role="button"],input,textarea,.dfa-card,.dfa-navbox,.dfa-chatbtn')));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    const loop = () => {
      x += (tx - x) * 0.22; y += (ty - y) * 0.22;
      el.style.transform = `translate(${x}px, ${y}px)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseover", onOver); };
  }, []);
  return (
    <div ref={ref} className={"dfa-cursor" + (hover ? " hover" : "")} aria-hidden>
      <svg width="24" height="28" viewBox="0 0 24 28" fill="none">
        <path
          d="M3 2 L3 23.5 L8.7 17.8 L12.3 25.6 L15.9 24 L12.3 16.2 L19.6 16.2 Z"
          fill="#a3e635"
          stroke="#0a140a"
          strokeWidth="1.1"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/** Bottom-right WhatsApp corong via Ms. Flo — jujur AI, bukan chat palsu. */
export function ChatWidgetPanel({
  pesan,
  onPesanChange,
  onEnter,
}: {
  pesan: string;
  onPesanChange: (v: string) => void;
  onEnter: (e: { key: string; preventDefault: () => void }) => void;
}) {
  const waHref = buildWaHref(pesan);
  return (
    <div style={{ width: 304, borderRadius: 22, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(8,8,8,0.94)", backdropFilter: "blur(12px)", overflow: "hidden", fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <span style={{ width: 38, height: 38, borderRadius: 999, background: LIME, color: VOID, display: "grid", placeItems: "center", fontWeight: 800, fontSize: 16 }}>F</span>
        <div>
          {/* Karakter AI sekarang; "dibuat dengan AI" wajib menyusul begitu kotak betul-betul menjawab (MsFloHidup.tsx:110, karakter-maker). */}
          <div style={{ fontSize: 14, fontWeight: 600, color: BONE, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            Ms. Flo
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.06em", color: SMOKE, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: "2px 6px" }}>
              Karakter AI
            </span>
          </div>
        </div>
      </div>
      <div style={{ padding: "16px 16px 6px" }}>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: ASH, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, borderTopLeftRadius: 4, padding: "12px 14px" }}>
          <p style={{ margin: 0 }}>{CHAT_SAPAAN_BARIS1}</p>
          <p style={{ margin: "10px 0 0" }}>{CHAT_SAPAAN_BARIS2}</p>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, padding: 14 }}>
        <input
          value={pesan}
          onChange={(e) => onPesanChange(e.target.value)}
          onKeyDown={onEnter}
          placeholder="Tulis pertanyaan Ibu atau Bapak…"
          style={{ flex: 1, fontFamily: FONT, fontSize: 13, color: BONE, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "10px 14px", outline: "none" }}
        />
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "10px 14px", borderRadius: 999, background: LIME, color: VOID, textDecoration: "none", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}
        >
          Kirim lewat WhatsApp
        </a>
      </div>
    </div>
  );
}

function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [pesan, setPesan] = useState("");
  const waHref = buildWaHref(pesan);
  return (
    <div style={{ position: "fixed", right: 22, bottom: 22, zIndex: 30, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 }}>
      {open && (
        <ChatWidgetPanel
          pesan={pesan}
          onPesanChange={setPesan}
          onEnter={(e) => tanganiEnterChat(e, waHref)}
        />
      )}
      <button className="dfa-chatbtn" onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 999, background: LIME, color: VOID, border: "none", fontFamily: FONT, fontSize: 14, fontWeight: 600 }}>
        <span style={{ width: 24, height: 24, borderRadius: 999, background: VOID, color: LIME, display: "grid", placeItems: "center", fontWeight: 800, fontSize: 13 }}>F</span>
        {open ? "Tutup" : "Chat via WhatsApp"}
      </button>
    </div>
  );
}

/** Full-viewport snap section. `side` places the text block left/right/center. */
function Panel({ id, refCb, side, children, innerStyle }: {
  id: string; refCb: (el: HTMLElement | null) => void; side: "left" | "right" | "center";
  children: React.ReactNode; innerStyle?: React.CSSProperties;
}) {
  const justify = side === "left" ? "flex-start" : side === "right" ? "flex-end" : "center";
  return (
    <section
      id={id}
      ref={refCb}
      className="dfa-panel"
      style={{ position: "relative", zIndex: 1, height: "100vh", scrollSnapAlign: "start", display: "flex", alignItems: "center", justifyContent: justify, boxSizing: "border-box", padding: "0 clamp(28px, 6vw, 96px)" }}
    >
      <div className="dfa-panel-inner" style={{ width: side === "center" ? "min(760px, 92%)" : "min(480px, 92%)", textAlign: side === "center" ? "center" : "left", ...innerStyle }}>
        {children}
      </div>
    </section>
  );
}

export function Landing() {
  const ctrlRef = useRef<Controller | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const panelsRef = useRef<(HTMLElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const idx = panelsRef.current.indexOf(e.target as HTMLElement);
            if (idx >= 0) { setActive(idx); ctrlRef.current?.setSection(idx); }
          }
        }
      },
      { root, threshold: 0.55 }
    );
    panelsRef.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const setPanel = (i: number) => (el: HTMLElement | null) => { panelsRef.current[i] = el; };
  const goTo = (i: number) => () => panelsRef.current[i]?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="dfa-landing" style={{ position: "relative", height: "100vh", color: BONE, fontFamily: FONT }}>
      <div style={{ position: "fixed", inset: 0, background: VOID, zIndex: -1 }} />
      <Particles ctrlRef={ctrlRef} />
      <CursorFX />
      <ChatWidget />

      <style>{`
        /* Boxed nav links */
        .dfa-navbox { color: ${ASH}; text-decoration: none; cursor: pointer; font-size: 13px; font-weight: 500;
          padding: 8px 16px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.03); transition: all .2s ease; }
        .dfa-navbox:hover { color: ${VOID}; background: ${LIME}; border-color: ${LIME}; }
        .dfa-cta { transition: opacity .2s ease, transform .2s ease; } .dfa-cta:hover { opacity: .9; transform: translateY(-1px); }
        .dfa-ghost:hover { border-color: ${LIME}; color: ${BONE}; }
        .dfa-dots button { transition: all .25s ease; }
        /* Hover-highlight cards */
        .dfa-card { transition: border-color .25s ease, background .25s ease, transform .25s ease; }
        .dfa-card:hover { border-color: ${LIME} !important; background: rgba(163,230,53,0.08) !important; transform: translateY(-3px); }
        .dfa-hero-row { box-sizing: border-box; }
        .dfa-msflo { order: 2; }
        @media (max-width: 819px) {
          .dfa-hero-row { flex-direction: column; align-items: flex-start !important; }
          .dfa-msflo { width: min(220px, 70vw) !important; }
        }
        /* Custom cursor (mouse only) */
        @media (pointer: fine) {
          .dfa-landing, .dfa-landing * { cursor: none !important; }
          /* JS sets translate() on this node; keep scale/glow on inner svg so they don't clash. */
          .dfa-cursor { position: fixed; top: 0; left: 0; pointer-events: none; z-index: 9999;
            will-change: transform;
            filter: drop-shadow(0 0 5px rgba(163,230,53,0.85)) drop-shadow(0 0 12px rgba(163,230,53,0.45));
            transition: filter .18s ease; }
          .dfa-cursor svg { display: block; transform: translate(-3px, -2px); transform-origin: 3px 2px;
            transition: transform .16s cubic-bezier(.2,.9,.3,1.3); }
          .dfa-cursor.hover { filter: drop-shadow(0 0 8px rgba(163,230,53,1)) drop-shadow(0 0 22px rgba(163,230,53,0.7)); }
          .dfa-cursor.hover svg { transform: translate(-3px, -2px) scale(1.45) rotate(-8deg); }
        }
        .dfa-chatbtn { transition: transform .2s ease, opacity .2s ease; }
        .dfa-chatbtn:hover { transform: translateY(-2px); }
        @media (max-width: 880px) {
          .dfa-nav-links { display: none !important; }
          .dfa-panel { justify-content: center !important; align-items: flex-end !important; padding-bottom: 96px !important; }
          .dfa-panel-inner { width: 92% !important; text-align: center !important; }
          .dfa-dots { display: none !important; }
          .dfa-cursor { display: none !important; }
        }
      `}</style>

      {/* NAV (fixed) */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 20 }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "18px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 12, height: 12, border: `1.5px solid ${LIME}`, borderRadius: 4, transform: "rotate(45deg)", display: "inline-block" }} />
            <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.02em" }}>DFA</span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 4,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
              color: "#1a1206", background: "linear-gradient(180deg,#f3d97a,#d4af37)",
              border: "1px solid #b8902a", borderRadius: 999, padding: "3px 9px",
            }}>
              <span style={{ width: 5, height: 5, background: "#1a1206", borderRadius: 999, display: "inline-block" }} />
              Anchor Bumi
            </span>
          </div>
          <div className="dfa-nav-links" style={{ display: "flex", gap: 12 }}>
            <a className="dfa-navbox" onClick={goTo(1)}>Apa itu DFA</a>
            <a className="dfa-navbox" onClick={goTo(3)}>Cara Kerja</a>
            <a className="dfa-navbox" onClick={goTo(4)}>Untuk Siapa</a>
          </div>
          <Link href="/login" className="dfa-cta" style={ctaPrimary as React.CSSProperties}>Masuk</Link>
        </div>
      </nav>

      {/* SECTION DOTS */}
      <div className="dfa-dots" style={{ position: "fixed", right: 24, top: "50%", transform: "translateY(-50%)", zIndex: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        {SECTIONS.map((_, i) => (
          <button key={i} onClick={goTo(i)} aria-label={`Bagian ${i + 1}`}
            style={{ width: 9, height: 9, minHeight: 9, padding: 0, borderRadius: 999, border: "none", cursor: "pointer", background: active === i ? LIME : "rgba(255,255,255,0.25)" }} />
        ))}
      </div>

      {/* SCROLL CONTAINER (snap) */}
      <div ref={scrollRef} style={{ height: "100vh", overflowY: "scroll", scrollSnapType: "y mandatory", position: "relative", zIndex: 1 }}>
        {/* 1 HERO — teks + Ms. Flo berdampingan; di <820px gambar di bawah teks */}
        <section
          id="s-hero"
          ref={setPanel(0)}
          className="dfa-panel"
          style={{ position: "relative", zIndex: 1, height: "100vh", scrollSnapAlign: "start", display: "flex", alignItems: "center", justifyContent: "flex-start", boxSizing: "border-box", padding: "0 clamp(28px, 6vw, 96px)" }}
        >
          <div
            className="dfa-hero-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "clamp(20px, 4vw, 48px)",
              width: "min(920px, 92%)",
              maxWidth: "100%",
            }}
          >
            <div style={{ flex: "1 1 280px", minWidth: 0, maxWidth: 480, textAlign: "left" }}>
              <Kicker>Analisis Sidik Jari Berbasis AI</Kicker>
              <h1 style={{ fontWeight: 200, fontSize: "clamp(46px, 6vw, 84px)", lineHeight: 0.92, letterSpacing: "-0.04em", margin: 0 }}>Pola jarimu,<br />peta potensimu.</h1>
              <p style={{ fontSize: 18, lineHeight: 1.5, color: ASH, maxWidth: "46ch", marginTop: 24 }}>
                DFA membaca pola dermatoglifik sepuluh jari untuk menyusun gambaran kecenderungan bawaan anak —
                salah satu referensi dalam memahami cara ia belajar. Disiapkan AI, divalidasi psikolog terlatih.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 36 }}>
                <a href={WA_HREF} target="_blank" rel="noreferrer" className="dfa-cta" style={ctaPrimary}>Konsultasi via WhatsApp</a>
                <a onClick={goTo(3)} className="dfa-ghost dfa-cta" style={{ ...ctaGhost, cursor: "pointer" }}>Lihat Cara Kerja</a>
              </div>
            </div>
            <MsFlo className="dfa-msflo" />
          </div>
        </section>

        {/* 2 APA ITU DFA */}
        <Panel id="s-apaitu" refCb={setPanel(1)} side="right">
          <Kicker>Apa itu DFA</Kicker>
          <h2 style={{ fontWeight: 200, fontSize: "clamp(34px,4.4vw,56px)", lineHeight: 1.05, letterSpacing: "-0.03em", margin: 0 }}>Bukan psikotes.<br />Peta bawaan lahir.</h2>
          <p style={{ fontSize: 18, lineHeight: 1.5, color: ASH, maxWidth: "52ch", marginTop: 24 }}>
            Dermatoglifik adalah ilmu pola guratan kulit jari — terbentuk sejak dalam kandungan, tak berubah
            seumur hidup. Justru karena itu ia bisa dibaca tanpa anak perlu menjawab apa pun: tidak ada yang
            bisa dilatih semalam, ditebak, atau dijawab asal-asalan.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.5, color: ASH, maxWidth: "52ch", marginTop: 16 }}>
            DFA memakainya sebagai titik awal, bukan vonis. Peta bawaan itu dibaca berdampingan dengan
            pengamatan nyata orang tua dan guru, lalu divalidasi psikolog terlatih.
          </p>
        </Panel>

        {/* 3 FILOSOFI */}
        <Panel id="s-filosofi" refCb={setPanel(2)} side="center">
          <h2 style={{ fontWeight: 200, fontSize: "clamp(34px,5vw,68px)", lineHeight: 1.05, letterSpacing: "-0.04em", margin: 0 }}>
            Setiap anak lahir dengan<br />peta potensinya sendiri.
          </h2>
        </Panel>

        {/* 4 CARA KERJA */}
        <Panel id="s-carakerja" refCb={setPanel(3)} side="right">
          <Kicker>Cara Kerja</Kicker>
          <h2 style={{ fontWeight: 200, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.05, letterSpacing: "-0.03em", margin: "0 0 28px" }}>Dari pindai ke pendampingan.</h2>
          <div style={{ display: "grid", gap: 14 }}>
            {[
              ["01", "Pindai", "Rekam sidik jari sepuluh jari dari tiga sisi."],
              ["02", "Analisis AI", "Model penglihatan mengenali 18 pola; algoritma menghitung sulur core→delta."],
              ["03", "Validasi Pakar", "Psikolog atau agen terlatih mengonfirmasi tiap jari."],
              ["04", "Laporan & Konsultasi", "Profil potensi + rekomendasi, siap dikonsultasikan."],
            ].map(([n, t, d]) => (
              <div key={n} style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
                <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.16em", color: LIME, minWidth: 24 }}>{n}</span>
                <div>
                  <div style={{ fontSize: 19, fontWeight: 400 }}>{t}</div>
                  <div style={{ fontSize: 15, lineHeight: 1.5, color: SMOKE, marginTop: 2 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        {/* 5 UNTUK SIAPA */}
        <Panel id="s-untuksiapa" refCb={setPanel(4)} side="center" innerStyle={{ marginTop: "26vh" }}>
          <Kicker>Untuk Siapa</Kicker>
          <h2 style={{ fontWeight: 200, fontSize: "clamp(30px,3.8vw,48px)", lineHeight: 1.05, letterSpacing: "-0.03em", margin: "0 0 32px" }}>Dari satu keluarga sampai satu sekolah.</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, textAlign: "left" }}>
            <div className="dfa-card" style={cardStyle}>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.16em", color: LIME }}>ORANG TUA & KELUARGA</div>
              <div style={{ fontSize: 22, marginTop: 12 }}>B2C</div>
              <p style={{ fontSize: 15, lineHeight: 1.5, color: ASH, marginTop: 12 }}>
                Kenali kecenderungan belajar anak sejak dini — satu bekal tambahan dalam pengasuhan dan memilih
                pendidikan, berdampingan dengan pengamatan Anda sendiri.
              </p>
            </div>
            <div className="dfa-card" style={cardStyle}>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.16em", color: LIME }}>SEKOLAH & KORPORAT</div>
              <div style={{ fontSize: 22, marginTop: 12 }}>B2B</div>
              <p style={{ fontSize: 15, lineHeight: 1.5, color: ASH, marginTop: 12 }}>
                Pemetaan kecenderungan massal sebagai bahan pertimbangan dalam pendampingan dan pengembangan SDM — dengan
                kapasitas approval terdistribusi per cabang.
              </p>
            </div>
          </div>
        </Panel>

        {/* 6 TIMELINE */}
        <Panel id="s-timeline" refCb={setPanel(5)} side="center">
          <Kicker>Perjalanan</Kicker>
          <h2 style={{ fontWeight: 200, fontSize: "clamp(32px,4.4vw,58px)", lineHeight: 1.05, letterSpacing: "-0.04em", margin: 0 }}>Profil yang tumbuh<br />bersama waktu.</h2>
          <p style={{ fontSize: 18, lineHeight: 1.5, color: ASH, maxWidth: "60ch", marginTop: 24, marginInline: "auto" }}>
            DFA bukan laporan sekali baca. Setiap fase tumbuh kembang anak menambah lapisan pemahaman —
            dari pemetaan awal hingga pendampingan berkelanjutan, satu garis waktu yang utuh.
          </p>
        </Panel>

        {/* 7 GLOBE + INDONESIA */}
        <Panel id="s-globe" refCb={setPanel(6)} side="center">
          <Kicker>Jangkauan</Kicker>
          <h2 style={{ fontWeight: 200, fontSize: "clamp(34px,4.6vw,62px)", lineHeight: 1.04, letterSpacing: "-0.04em", margin: 0 }}>
            18+ Tahun · 8.000+ Klien<br />Seluruh Indonesia.
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.5, color: ASH, maxWidth: "52ch", marginTop: 24, marginInline: "auto" }}>
            Dari Sumatera sampai Papua — pengalaman dermatoglifik DCPPI memetakan potensi
            ribuan anak dan keluarga di seluruh nusantara.
          </p>
        </Panel>

        {/* 8 CTA + FOOTER */}
        <Panel id="s-cta" refCb={setPanel(7)} side="center">
          <h2 style={{ fontWeight: 200, fontSize: "clamp(38px,5.5vw,76px)", lineHeight: 1.0, letterSpacing: "-0.04em", margin: 0 }}>Siap memetakan potensi?</h2>
          <p style={{ fontSize: 18, color: ASH, marginTop: 18, maxWidth: "44ch", marginInline: "auto" }}>
            Bicara langsung dengan konsultan DCPPI untuk memulai analisis sidik jari.
          </p>
          <div style={{ marginTop: 32 }}>
            <a href={WA_HREF} target="_blank" rel="noreferrer" className="dfa-cta" style={{ ...ctaPrimary, padding: "16px 28px" }}>Konsultasi via WhatsApp</a>
          </div>
          <div style={{ marginTop: 64, fontSize: 13, color: SMOKE, display: "flex", gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ width: 9, height: 9, border: `1.5px solid ${LIME}`, borderRadius: 3, transform: "rotate(45deg)", display: "inline-block" }} />
            <span>DFA — DCPPI · Pilar Nawa Group</span>
          </div>
        </Panel>
      </div>
    </div>
  );
}
